import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Circle, Mail, Trash2, Link2, Flag, ClipboardList, PenLine } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import VerifiedBadge from "@/components/VerifiedBadge";
import { CreateExternalEvent, ImportParticipants } from "@/components/ratings/ExternalEventTools";
import api, { formatApiError } from "@/lib/api";

const STEPS = ["Create Event", "Import Participants", "Match Player Profiles", "Invite Players", "Rate Participants", "View Submission Status", "Rating History", "Report Rating Abuse"];
const TYPE = { tournament: "PointControl Tournament", club_war: "PointControl Club Wars", external: "External Event" };

function MatchPicker({ eventId, pid, onDone }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setOpts([]); return; }
    const t = setTimeout(() => api.get("/ratings/directory", { params: { q, sort: "name" } }).then(({ data }) => setOpts(data.slice(0, 6))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const pick = async (uid) => { await api.patch(`/ratings/external-events/${eventId}/participants/${pid}`, { user_id: uid }); toast.success("Matched to player profile"); onDone(); };
  return (
    <div className="relative">
      <input data-testid={`match-search-${pid}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find profile…" className="rounded-lg glass px-2 py-1 text-xs w-36" style={{ color: "var(--text)" }} />
      {opts.length > 0 && (
        <div className="absolute z-20 mt-1 w-56 rounded-xl glow-border p-1" style={{ background: "var(--surface)" }}>
          {opts.map((o) => <button key={o.user_id} data-testid={`match-opt-${o.user_id}`} onClick={() => pick(o.user_id)} className="w-full text-left text-xs px-2 py-1.5 rounded-lg hover:bg-black/10">{o.name} <span className="mono" style={{ color: "var(--text-2)" }}>{o.player_code}</span></button>)}
        </div>
      )}
    </div>
  );
}

function Participant({ ev, p, reload }) {
  const nav = useNavigate();
  const ext = ev.type === "external";
  const invite = async () => { try { await api.post(`/ratings/external-events/${ev.id}/participants/${p.pid}/invite`); toast.success("Invite sent"); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const remove = async () => { await api.delete(`/ratings/external-events/${ev.id}/participants/${p.pid}`); reload(); };
  return (
    <div className="py-2 flex items-center gap-2 text-sm flex-wrap" data-testid={`participant-${p.user_id || p.pid}`}>
      {p.rated ? <CheckCircle2 className="w-4 h-4" style={{ color: "#22c55e" }} /> : <Circle className="w-4 h-4" style={{ color: "var(--text-2)" }} />}
      <span className="font-semibold">{p.name}</span><VerifiedBadge verified={p.verified} />
      {ext && <span className="text-[10px] mono uppercase glass rounded-full px-2 py-0.5" style={{ color: p.user_id ? "#22c55e" : "#f59e0b" }}>{p.user_id ? "matched" : p.status}</span>}
      <span className="ml-auto flex items-center gap-2">
        {ext && !p.user_id && <MatchPicker eventId={ev.id} pid={p.pid} onDone={reload} />}
        {ext && !p.user_id && <button data-testid={`invite-${p.pid}`} onClick={invite} title={p.email ? "Send invite" : "Add an email to invite"} className="rounded-full glass px-3 py-1 text-xs font-bold flex items-center gap-1"><Mail className="w-3 h-3 text-primary-c" />{p.invited_at ? "Re-invite" : "Invite"}</button>}
        {p.user_id && !p.rated && <button data-testid={`rate-participant-${p.user_id}`} onClick={() => nav(`/rate/${p.user_id}`)} className="btn-glow rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1"><PenLine className="w-3 h-3" />Rate</button>}
        {p.rated && <button data-testid={`update-participant-${p.user_id}`} onClick={() => nav(`/rate/${p.user_id}`)} className="rounded-full glass px-3 py-1 text-xs font-bold flex items-center gap-1"><PenLine className="w-3 h-3 text-primary-c" />Update</button>}
        {ext && <button data-testid={`remove-${p.pid}`} onClick={remove}><Trash2 className="w-3.5 h-3.5" style={{ color: "var(--text-2)" }} /></button>}
      </span>
    </div>
  );
}

function EventCard({ ev, reload }) {
  const total = ev.participants.filter((p) => p.user_id).length;
  const pct = total ? Math.round((ev.rated_count / total) * 100) : 0;
  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid={`org-event-${ev.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold">{ev.name}</div>
          <div className="text-xs" style={{ color: "var(--text-2)" }}>{TYPE[ev.type]}{ev.platform ? ` · ${ev.platform}` : ""} · {(ev.date || "").slice(0, 10)}{ev.venue ? ` · ${ev.venue}` : ""}{ev.category ? ` · ${ev.category}` : ""}{ev.status === "flagged" ? " · FLAGGED" : ""}</div>
        </div>
        <span className="text-xs mono" data-testid={`org-event-progress-${ev.id}`}>{ev.rated_count}/{total} rated</span>
        <div className="w-28 h-1.5 rounded-full" style={{ background: "var(--border)" }}><div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--primary)" }} /></div>
      </div>
      {ev.type === "external" && <div className="mt-4"><ImportParticipants eventId={ev.id} onDone={reload} /></div>}
      <div className="mt-3 divide-y" style={{ borderColor: "var(--border)" }}>
        {ev.participants.length === 0 ? <div className="text-xs py-3" style={{ color: "var(--text-2)" }}>No linked player accounts yet.</div>
          : ev.participants.map((p) => <Participant key={p.pid || p.user_id} ev={ev} p={p} reload={reload} />)}
      </div>
    </div>
  );
}

function Steps({ events, history }) {
  const ext = events.filter((e) => e.type === "external");
  const done = [events.length > 0, ext.some((e) => e.participants.length) || events.some((e) => e.participants.length),
    events.some((e) => e.participants.some((p) => p.user_id)), ext.some((e) => e.participants.some((p) => p.invited_at)) || ext.every((e) => e.participants.every((p) => p.user_id)),
    history.length > 0, history.length > 0, history.length > 0, false];
  return (
    <div className="flex flex-wrap gap-2" data-testid="organizer-steps">
      {STEPS.map((s, i) => (
        <span key={s} className="text-[11px] font-bold rounded-full px-3 py-1.5 flex items-center gap-1.5 glass" data-testid={`step-${i}`} style={{ color: done[i] ? "#22c55e" : "var(--text-2)" }}>
          {done[i] ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="mono">{i + 1}</span>}{s}
        </span>
      ))}
    </div>
  );
}

export default function OrganizerRatings() {
  const [events, setEvents] = useState(null);
  const [history, setHistory] = useState([]);
  const load = useCallback(() => {
    api.get("/ratings/organizer/events").then(({ data }) => setEvents(data)).catch(() => setEvents(false));
    api.get("/ratings/organizer/history").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const report = async () => {
    const reason = window.prompt("Describe the rating abuse (player, link or event involved):");
    if (!reason) return;
    try { await api.post("/ratings/reports", { target_type: "player", target_id: "organizer-report", reason }); toast.success("Report sent to administrators"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Rate Participants" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center gap-2"><ClipboardList className="w-6 h-6 text-primary-c" /><h1 className="font-display font-bold text-2xl">Organizer Ratings</h1>
          <button data-testid="report-abuse-btn" onClick={report} className="ml-auto rounded-full glass px-4 py-2 text-xs font-bold flex items-center gap-1.5"><Flag className="w-3.5 h-3.5" style={{ color: "#ef4444" }} />Report Rating Abuse</button></div>
        <p className="text-sm" style={{ color: "var(--text-2)" }}>Your ratings of players in events you organized count as <b>Organizer / Tournament Director (50%)</b>. As organizer you can update your ratings anytime.</p>
        {events === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div> : events === false ? <p className="text-sm" style={{ color: "#ef4444" }}>Organizer access only.</p> : (
          <>
            <Steps events={events} history={history} />
            <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
              <div className="font-display font-bold mb-3 flex items-center gap-2"><Link2 className="w-4 h-4 text-primary-c" />New external event <span className="text-xs font-normal" style={{ color: "var(--text-2)" }}>run on other tournament software</span></div>
              <CreateExternalEvent onCreated={load} />
            </div>
            {events.map((ev) => <EventCard key={`${ev.type}-${ev.id}`} ev={ev} reload={load} />)}
            <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="organizer-history">
              <div className="font-display font-bold mb-2">Rating History</div>
              {history.length === 0 ? <div className="text-xs" style={{ color: "var(--text-2)" }}>You haven't submitted any ratings yet.</div> : history.map((h) => (
                <div key={h.rating_id} className="py-2 flex items-center gap-2 text-sm border-b last:border-0" style={{ borderColor: "var(--border)" }}>
                  <span className="font-semibold flex-1">{h.player_name}</span><span className="text-xs" style={{ color: "var(--text-2)" }}>{h.event || "—"}</span>
                  <span className="mono">{Number(h.overall).toFixed(2)}</span><span className="text-[10px] mono uppercase">{h.category}</span><span className="text-[11px] mono" style={{ color: "var(--text-2)" }}>{h.at.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
